import {
  complexDurationAndAttendeesServices,
  defaultService,
  extendedSingleDurationPaidService,
  multipleServices,
  multiTierService,
  poaService,
  serviceNoDescription,
  serviceWithUnsafeDescription,
  unavailableService,
} from "./service";
import {
  defaultOrganisation,
  organisationForcedTimezone,
  tenOrganisationsPage,
  testOrganisationWithFeatureFlag,
  twoOrganisationsDifferentTimezonePage,
  twoOrganisationsPage,
} from "./organisation";
import {
  GetBookingToolDataDocument,
  GetBookingToolDataQuery,
  GetBookingToolDataQueryVariables,
  GetBookingToolScopedServicesDocument,
  GetBookingToolScopedServicesQuery,
  GetBookingToolSettingsDocument,
  GetBookingToolSettingsQuery,
  BrandTheme,
  ResourceSelectionMode,
  UnitOfMeasurement,
} from "../GeneratedGraphQL";

import { BookingTool } from "../AppDataProvider/AppDataProvider";
import { createPageInfo } from "./pageInfo";
import { MockedResponse } from "@apollo/client/testing";

/**
 * `defaultService` doesn't set every optional field GetBookingToolSettings'
 * `services` selection asks for (tiers, parts, maxParticipantsPerBooking,
 * isDiscountCodeAvailable, durations[].default, durations[].POA) — each
 * simply absent (`undefined`) rather than explicitly `null`. For most mocks
 * that's harmless, but Apollo's InMemoryCache treats a requested-but-
 * `undefined` field as an incomplete read, which can silently leave
 * `useQuery`'s `data` `undefined` (with no visible error) once something
 * else in the tree depends on that read being complete — e.g.
 * ScopedBookingToolData rendering all the way through to
 * LocationAvailabilityStep. Explicitly nulling these out avoids that.
 */
const fullyPopulatedDefaultService = {
  ...defaultService,
  durations: defaultService.durations.map((duration) => ({
    default: null,
    POA: null,
    ...duration,
  })),
  isDiscountCodeAvailable: false,
  maxParticipantsPerBooking: null,
  parts: null,
  tiers: null,
};

export const defaultBookingTool = {
  _id: "1234567890abcdef12345678",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: false },
  mapkitAuthenticationToken: "testToken",
  name: "Test Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  percentageThresholdForShowingRemainingNumberOfSpaces: 15,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};
export const extendedServiceBookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [
      {
        cursor: extendedSingleDurationPaidService._id,
        node: extendedSingleDurationPaidService,
      },
    ],
    pageInfo: createPageInfo(),
  },
};

export const forcePhoneValidationBookingTool = {
  ...defaultBookingTool,
  _id: "2234567890abcdef12345333",
  forcePhoneValidationToCountry: "GB",
};

/**
 * A booking tool configured with a light-theme brand palette, for exercising
 * the booking widget's brand-customisation (see docs/booking-widget-theming.md).
 */
export const lightBrandedBookingTool = {
  ...defaultBookingTool,
  _id: "3234567890abcdef12345444",
  customColours: {
    accentColour: "#c98a2b",
    logoUrl: "https://placehold.co/160x40/222/fff?text=Brand",
    pageBackground: "#f6f1e8",
    primary: "#1f6f5c",
    primaryActionColour: "#1f6f5c",
    primaryLightColour: "#dcefe9",
    secondary: "#c98a2b",
    theme: BrandTheme.Light,
    __typename: "CustomColours" as const,
  },
};

/**
 * A booking tool configured with a dark-theme brand palette, for exercising
 * the booking widget's brand-customisation (see docs/booking-widget-theming.md).
 */
export const darkBrandedBookingTool = {
  ...defaultBookingTool,
  _id: "4234567890abcdef12345555",
  customColours: {
    accentColour: "#4fc9f5",
    logoUrl: "https://placehold.co/160x40/17151b/f3eee6?text=Brand",
    pageBackground: "#0c0a10",
    primary: "#d8a12a",
    primaryActionColour: "#d8a12a",
    primaryLightColour: "#f1dfb0",
    secondary: "#4fc9f5",
    theme: BrandTheme.Dark,
    __typename: "CustomColours" as const,
  },
};

export const defaultBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: defaultBookingTool._id },
    },
    result: { data: { bookingTool: defaultBookingTool } },
  };

export const extendedServiceBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: extendedServiceBookingTool._id },
    },
    result: { data: { bookingTool: extendedServiceBookingTool } },
  };

export const lightBrandedBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: lightBrandedBookingTool._id },
    },
    result: { data: { bookingTool: lightBrandedBookingTool } },
  };

export const darkBrandedBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: darkBrandedBookingTool._id },
    },
    result: { data: { bookingTool: darkBrandedBookingTool } },
  };

export const multiServiceBookingTool = {
  _id: "1234567890abcdef123456784",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: false },
  mapkitAuthenticationToken: "testToken",
  name: "Multi Service Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [
      { cursor: defaultService._id, node: defaultService },
      { cursor: multipleServices[0]._id, node: multipleServices[0] },
    ],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const multiServiceBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: multiServiceBookingTool._id },
    },
    result: { data: { bookingTool: multiServiceBookingTool } },
  };

export const singleOrgServiceBookingTool: BookingTool = {
  _id: "1234567890abcdef12345679",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: false },
  mapkitAuthenticationToken: "testToken",
  name: "Test Booking Tool, single org & service",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const tenOrganisationBookingTool = {
  _id: "1234567890abcdef12345681",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: {
    includedCountries: ["UK"],
    maxLocationSearchRange: 900,
    maxLocationsToDisplay: 8,
    minLocationsToDisplay: 3,
    overrideLocationSearchQueryMapping: [
      { from: "Hamilton", to: "Hamilton, Scotland" },
    ],
    searchAndMapEnabled: true,
  },
  mapkitAuthenticationToken: "testToken",
  name: "Ten Org Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: tenOrganisationsPage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const tenOrganisationsBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  tenOrganisationsPage.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: tenOrganisationBookingTool._id,
    };
    if (i !== 0) {
      variables.organisationCursor = tenOrganisationsPage.edges[i - 1].cursor;
    }

    return {
      request: {
        query: GetBookingToolDataDocument,
        variables,
      },
      result: {
        data: {
          bookingTool: {
            ...tenOrganisationBookingTool,
            organisations: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: tenOrganisationsPage.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });

/**
 * BookingToolData now fetches GetBookingToolSettings first
 * whenever an `organisationId` is prefilled, to decide whether
 * organisationAvailabilityFlow is enabled before deciding whether to fetch
 * every location via GetBookingToolData. tenOrganisationBookingTool has
 * organisationAvailabilityFlow: false, so this mock lets the component
 * correctly fall through to the existing full-fetch behaviour (rather than
 * erroring out with no matching mock at all).
 *
 * GetBookingToolSettings is now flags/settings-only (no `services`), so
 * there's no `serviceFilters` variable or `services` result here — see
 * `tenOrganisationBookingToolScopedServicesRequest` below for the
 * booking-tool-scoped services mock (only needed when
 * organisationAvailabilityFlow is true, which it isn't for this fixture).
 */
export const tenOrganisationBookingToolSettingsRequest: MockedResponse<GetBookingToolSettingsQuery> =
  {
    request: {
      query: GetBookingToolSettingsDocument,
      variables: {
        id: tenOrganisationBookingTool._id,
      },
    },
    result: {
      data: {
        bookingTool: {
          _id: tenOrganisationBookingTool._id,
          brand: null,
          customColours: null,
          forceOrganisationSelection:
            tenOrganisationBookingTool.forceOrganisationSelection,
          forcePhoneValidationToCountry: null,
          forceServiceSelection:
            tenOrganisationBookingTool.forceServiceSelection,
          fonts: null,
          hideAppointeddFooter: null,
          locationSelection: tenOrganisationBookingTool.locationSelection,
          mapkitAuthenticationToken:
            tenOrganisationBookingTool.mapkitAuthenticationToken,
          name: tenOrganisationBookingTool.name,
          organisationAvailabilityFlow:
            tenOrganisationBookingTool.organisationAvailabilityFlow,
          percentageThresholdForShowingRemainingNumberOfSpaces: null,
          resourceSelectionMode:
            tenOrganisationBookingTool.resourceSelectionMode,
          unitOfMeasurement: tenOrganisationBookingTool.unitOfMeasurement,
        },
      },
    },
  };

/**
 * Mock for the new GetBookingToolScopedServices query — only fired once
 * GetBookingToolSettings confirms organisationAvailabilityFlow is true.
 * Not actually exercised for tenOrganisationBookingTool (its
 * organisationAvailabilityFlow is false), but kept here alongside the
 * settings mock for symmetry/future use.
 */
export const tenOrganisationBookingToolScopedServicesRequest: MockedResponse<GetBookingToolScopedServicesQuery> =
  {
    request: {
      query: GetBookingToolScopedServicesDocument,
      variables: {
        id: tenOrganisationBookingTool._id,
        serviceFilters: {
          organisationIds: [tenOrganisationsPage.edges[0].node._id],
          showInvisibleIfOnlyInvisibleSelected: true,
          visibleOnly: true,
        },
      },
    },
    result: {
      data: {
        bookingTool: {
          services: {
            edges: [
              {
                cursor: fullyPopulatedDefaultService._id,
                node: fullyPopulatedDefaultService,
              },
            ],
            pageInfo: createPageInfo(),
          },
        },
      },
    },
  };

export const tenOrganisationNoLocationRestrictionBookingTool = {
  _id: "1234567890abcdef12345682",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: true },
  mapkitAuthenticationToken: "testToken",
  name: "Ten Org - No Location Restrictions Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: tenOrganisationsPage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const tenOrganisationNoLocationRestrictionBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  tenOrganisationsPage.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: tenOrganisationNoLocationRestrictionBookingTool._id,
    };
    if (i !== 0) {
      variables.organisationCursor = tenOrganisationsPage.edges[i - 1].cursor;
    }

    return {
      request: {
        query: GetBookingToolDataDocument,
        variables,
      },
      result: {
        data: {
          bookingTool: {
            ...tenOrganisationNoLocationRestrictionBookingTool,
            organisations: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: tenOrganisationsPage.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });

export const genericBookingTool = {
  _id: "GENERIC_BOOKING_TOOL",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  isGenericBookingTool: true,
  mapkitAuthenticationToken: "testToken",
  name: "Genetic Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [
      { cursor: defaultService._id, node: defaultService },
      { cursor: multipleServices[0]._id, node: multipleServices[0] },
    ],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const genericBookingToolSingleService = {
  _id: "GENERIC_BOOKING_TOOL",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  isGenericBookingTool: true,
  mapkitAuthenticationToken: "testToken",
  name: "Genetic Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const noServicesBookingTool = {
  _id: "1234567890abcdef12345643",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  mapkitAuthenticationToken: "testToken",
  name: "No Services Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const noServicesBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: noServicesBookingTool._id },
    },
    result: { data: { bookingTool: noServicesBookingTool } },
  };

export const multiServicesBookingTool = {
  _id: "multiple pages of services",
  forceOrganisationSelection: false,
  forceServiceSelection: true,
  mapkitAuthenticationToken: "testToken",
  name: "Booking Tool with lots of services",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: multipleServices.map((service) => ({
      cursor: service._id,
      node: service,
    })),
    pageInfo: createPageInfo({ total: multipleServices.length }),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const poaServicesBookingTool = {
  _id: "poa service",
  forceOrganisationSelection: false,
  forceServiceSelection: true,
  mapkitAuthenticationToken: "testToken",
  name: "Booking Tool with poa service",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [poaService].map((service) => ({
      cursor: service._id,
      node: service,
    })),
    pageInfo: createPageInfo({ total: 1 }),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const multiPageServicesBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  multiServicesBookingTool.services.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: multiServicesBookingTool._id,
    };
    if (i !== 0) {
      variables.serviceCursor =
        multiServicesBookingTool.services.edges[i - 1].cursor;
    }

    return {
      request: { query: GetBookingToolDataDocument, variables },
      result: {
        data: {
          bookingTool: {
            ...multiServicesBookingTool,
            services: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: multiServicesBookingTool.services.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });

export const noOrganisationNoServicesBookingTool = {
  _id: "2234567890abcdef12345689",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  mapkitAuthenticationToken: "testToken",
  name: "No Organisation Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: { edges: [], pageInfo: createPageInfo({ total: 0 }) },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const noOrganisationNoServicesBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: noOrganisationNoServicesBookingTool._id },
    },
    result: { data: { bookingTool: noOrganisationNoServicesBookingTool } },
  };

export const complexDurationAndAttendeesBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: complexDurationAndAttendeesServices.map((service) => ({
      cursor: service._id,
      node: service,
    })),
    pageInfo: createPageInfo({
      total: complexDurationAndAttendeesServices.length,
    }),
  },
};

export const multiTierServiceBookingTool: BookingTool = {
  _id: "1234567890abcdef12345688",
  forceOrganisationSelection: false,
  forceServiceSelection: true,
  mapkitAuthenticationToken: "testToken",
  name: "Booking Tool with 1 multi-tier service",
  organisationAvailabilityFlow: false,
  organisations: {
    edges: [{ cursor: defaultOrganisation._id, node: defaultOrganisation }],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: multiTierService._id, node: multiTierService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const bookingToolOrgForcedTimezone = {
  ...defaultBookingTool,
  organisations: {
    edges: [
      {
        cursor: organisationForcedTimezone._id,
        node: organisationForcedTimezone,
      },
    ],
    pageInfo: createPageInfo(),
  },
};

export const bookingToolForcedTimezoneRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: defaultBookingTool._id },
    },
    result: { data: { bookingTool: bookingToolOrgForcedTimezone } },
  };
export const serviceUnavailableBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [{ cursor: unavailableService._id, node: unavailableService }],
    pageInfo: createPageInfo(),
  },
};

export const serviceNoDescriptionBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [{ cursor: serviceNoDescription._id, node: serviceNoDescription }],
    pageInfo: createPageInfo(),
  },
};

export const serviceWithUnsafeDescriptionBookingTool: BookingTool = {
  ...defaultBookingTool,
  services: {
    edges: [
      {
        cursor: serviceWithUnsafeDescription._id,
        node: serviceWithUnsafeDescription,
      },
    ],
    pageInfo: createPageInfo(),
  },
};

export const organisationWithMapAvailabilityBookingTool = {
  _id: "1234567890abcdef12345679",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: { searchAndMapEnabled: true },
  mapkitAuthenticationToken: "testToken",
  multiSlotBookingFlowEnabled: true,
  name: "Test Booking Tool, single org & service",
  organisationAvailabilityFlow: true,
  organisations: {
    edges: [
      {
        cursor: testOrganisationWithFeatureFlag._id,
        node: testOrganisationWithFeatureFlag,
      },
    ],
    pageInfo: createPageInfo(),
  },
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const organisationWithMapAvailabilityBookingToolRequest: MockedResponse<GetBookingToolDataQuery> =
  {
    request: {
      query: GetBookingToolDataDocument,
      variables: { id: organisationWithMapAvailabilityBookingTool._id },
    },
    result: {
      data: { bookingTool: organisationWithMapAvailabilityBookingTool },
    },
  };

/**
 * mock for the new GetBookingToolSettings query used by
 * BookingToolData's scoped-organisation fast path. This booking tool has
 * organisationAvailabilityFlow: true, so a request for
 * these settings should lead the component to fetch the prefilled
 * organisation's details (via GetOrganisationDetails) and its
 * booking-tool-scoped service (via GetBookingToolScopedServices, filtered
 * down to the prefilled organisation via `serviceFilters.organisationIds`)
 * in parallel, rather than from Organisation.services — see
 * BookingToolData.tsx for why that distinction matters. GetBookingToolSettings
 * itself is flags/settings-only (no `services`), so there's no
 * `serviceFilters` variable or `services` result here.
 */
export const organisationWithMapAvailabilityBookingToolSettingsRequest: MockedResponse<GetBookingToolSettingsQuery> =
  {
    request: {
      query: GetBookingToolSettingsDocument,
      variables: {
        id: organisationWithMapAvailabilityBookingTool._id,
      },
    },
    result: {
      data: {
        bookingTool: {
          _id: organisationWithMapAvailabilityBookingTool._id,
          brand: null,
          customColours: null,
          forceOrganisationSelection:
            organisationWithMapAvailabilityBookingTool.forceOrganisationSelection,
          forcePhoneValidationToCountry: null,
          forceServiceSelection:
            organisationWithMapAvailabilityBookingTool.forceServiceSelection,
          fonts: null,
          hideAppointeddFooter: null,
          locationSelection: {
            includedCountries: null,
            maxLocationSearchRange: null,
            maxLocationsToDisplay: null,
            minLocationsToDisplay: null,
            overrideLocationSearchQueryMapping: null,
            searchAndMapEnabled:
              organisationWithMapAvailabilityBookingTool.locationSelection
                .searchAndMapEnabled,
          },
          mapkitAuthenticationToken:
            organisationWithMapAvailabilityBookingTool.mapkitAuthenticationToken,
          name: organisationWithMapAvailabilityBookingTool.name,
          organisationAvailabilityFlow:
            organisationWithMapAvailabilityBookingTool.organisationAvailabilityFlow,
          percentageThresholdForShowingRemainingNumberOfSpaces: null,
          resourceSelectionMode:
            organisationWithMapAvailabilityBookingTool.resourceSelectionMode,
          unitOfMeasurement:
            organisationWithMapAvailabilityBookingTool.unitOfMeasurement,
        },
      },
    },
  };

/**
 * mock for the new GetBookingToolScopedServices query — fired by
 * ScopedBookingToolDataLoader in parallel with GetOrganisationDetails, once
 * GetBookingToolSettings has confirmed organisationAvailabilityFlow is
 * true.
 */
export const organisationWithMapAvailabilityBookingToolScopedServicesRequest: MockedResponse<GetBookingToolScopedServicesQuery> =
  {
    request: {
      query: GetBookingToolScopedServicesDocument,
      variables: {
        id: organisationWithMapAvailabilityBookingTool._id,
        serviceFilters: {
          organisationIds: [testOrganisationWithFeatureFlag._id],
          showInvisibleIfOnlyInvisibleSelected: true,
          visibleOnly: true,
        },
      },
    },
    result: {
      data: {
        bookingTool: {
          services: {
            edges: [
              {
                cursor: fullyPopulatedDefaultService._id,
                node: fullyPopulatedDefaultService,
              },
            ],
            pageInfo: createPageInfo(),
          },
        },
      },
    },
  };

export const twoOrganisationBookingTool = {
  _id: "1234567890abcdef12345681",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: {
    includedCountries: ["UK"],
    maxLocationSearchRange: 900,
    maxLocationsToDisplay: 5,
    minLocationsToDisplay: 3,
    overrideLocationSearchQueryMapping: [
      { from: "Hamilton", to: "Hamilton, Scotland" },
    ],
    searchAndMapEnabled: true,
  },
  mapkitAuthenticationToken: "testToken",
  name: "Two Org Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: twoOrganisationsPage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const twoOrganisationBookingDifferentTimezonesTool: BookingTool = {
  _id: "1234567890abcdef12345681",
  forceOrganisationSelection: false,
  forceServiceSelection: false,
  locationSelection: {
    includedCountries: ["UK"],
    maxLocationSearchRange: 900,
    maxLocationsToDisplay: 5,
    minLocationsToDisplay: 3,
    overrideLocationSearchQueryMapping: [
      { from: "Hamilton", to: "Hamilton, Scotland" },
    ],
    searchAndMapEnabled: true,
  },
  mapkitAuthenticationToken: "testToken",
  name: "Two Org Booking Tool",
  organisationAvailabilityFlow: false,
  organisations: twoOrganisationsDifferentTimezonePage,
  resourceSelectionMode: ResourceSelectionMode.Disabled,
  services: {
    edges: [{ cursor: defaultService._id, node: defaultService }],
    pageInfo: createPageInfo(),
  },
  unitOfMeasurement: UnitOfMeasurement.Miles,
};

export const twoOrganisationsBookingToolRequests: MockedResponse<GetBookingToolDataQuery>[] =
  twoOrganisationsPage.edges.map((edge, i) => {
    const variables: GetBookingToolDataQueryVariables = {
      id: twoOrganisationBookingTool._id,
    };
    if (i !== 0) {
      variables.organisationCursor = twoOrganisationsPage.edges[i - 1].cursor;
    }

    return {
      request: {
        query: GetBookingToolDataDocument,
        variables,
      },
      result: {
        data: {
          bookingTool: {
            ...twoOrganisationBookingTool,
            organisations: {
              edges: [edge],
              /* eslint-disable sort-keys */
              pageInfo: {
                hasPreviousPage: false,
                hasNextPage: true,
                total: twoOrganisationsPage.edges.length,
                startCursor: edge.cursor,
                endCursor: edge.cursor,
              },
              /* eslint-enable sort-keys */
            },
          },
        },
      },
    };
  });
